import { fetchAuthSession } from 'aws-amplify/auth';
import { autoTagCycleFields, batchTagCycleFields } from '../lib/utils/cycleTagging';
import dayjs from 'dayjs';
import mockDataService from './mockDataService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Check if we should use mock data
const shouldUseMockData = () => {
  // Force mock data if emergency access is being used
  if (typeof window !== 'undefined' && localStorage.getItem('force_mock_data') === 'true') {
    return true;
  }
  
  return process.env.REACT_APP_FORCE_MOCK_DATA === 'true' || 
         (process.env.NODE_ENV === 'development' && !API_BASE_URL);
};

// Function to get authenticated headers
const getAuthHeaders = async () => {
  try {
    const session = await fetchAuthSession();
    const headers = {
      'Content-Type': 'application/json',
    };
    
    // Add Authorization header if we have tokens
    if (session.tokens?.idToken) {
      headers['Authorization'] = `Bearer ${session.tokens.idToken.toString()}`;
    } else {
      // In hosted dev, this should trigger fallback to DynamoDB
      if (window.location.hostname === 'dev.d1d7vib6srssm3.amplifyapp.com') {
        throw new Error('No authentication token available in hosted dev - should use DynamoDB fallback');
      }
    }
    
    return headers;
  } catch (error) {
    // For hosted dev, we want this to fail fast so it falls back to DynamoDB
    if (window.location.hostname === 'dev.d1d7vib6srssm3.amplifyapp.com') {
      throw error;
    }
    
    return {
      'Content-Type': 'application/json',
    };
  }
};

// API request wrapper with error handling
const apiRequest = async (url, options = {}) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || `HTTP ${response.status}: ${response.statusText}`;
      } catch {
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    // Handle 204 No Content responses
    if (response.status === 204) {
      return null;
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

export const apiCEUService = {
  // Get all CEUs for a user
  async getCEUs(userId) {
    if (shouldUseMockData()) {
      return mockDataService.getCEUs(userId);
    }

    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }

    try {
      const response = await apiRequest(`${API_BASE_URL}/users/${userId}/ceus`);
      
      // Handle the Lambda response format: { ceus: [...], count: N, authenticatedUser: {...} }
      const ceus = response?.ceus || response || [];
      
      // Get user profile for cycle tagging
      let userProfile = null;
      try {
        const userResponse = await apiRequest(`${API_BASE_URL}/users/${userId}`);
        userProfile = userResponse;
      } catch (userError) {
        console.warn('Could not fetch user profile for cycle tagging:', userError.message);
      }
      
      // Apply cycle tagging with user profile
      const taggedCEUs = userProfile ? batchTagCycleFields(ceus, userProfile) : ceus;
      
      return { success: true, data: taggedCEUs };
    } catch (error) {
      // Log errors only in development
    if (process.env.NODE_ENV === 'development') {
      console.error('❌ Error fetching CEUs:', error);
    }
      
      // If it's an auth error in hosted dev, try to fallback gracefully
      if (error.message.includes('Unauthorized') || error.message.includes('Internal server error')) {
        try {
          const mockResult = await mockDataService.getCEUs(userId);
          return mockResult;
        } catch (mockError) {
          // Continue to error return
        }
      }
      
      // Return error in expected format for unified service
      return { 
        success: false, 
        error: error.message, 
        data: [] 
      };
    }
  },

  // Get specific CEU
  async getCEU(userId, ceuId) {
    if (shouldUseMockData()) {
      return mockDataService.getCEU(userId, ceuId);
    }

    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }

    try {
      const ceu = await apiRequest(`${API_BASE_URL}/users/${userId}/ceus/${ceuId}`);
      
      if (ceu) {
        // Get user profile for cycle tagging
        let userProfile = null;
        try {
          const userResponse = await apiRequest(`${API_BASE_URL}/users/${userId}`);
          userProfile = userResponse;
        } catch (userError) {
          console.warn('Could not fetch user profile for cycle tagging:', userError.message);
        }
        
        // Apply cycle tagging with user profile
        return userProfile ? autoTagCycleFields(ceu, userProfile) : ceu;
      }
      
      return null;
    } catch (error) {
      if (error.message.includes('404')) {
        return null; // CEU not found
      }
      throw error;
    }
  },

  // Create CEU
  async createCEU(userId, ceuData) {
    if (shouldUseMockData()) {
      return mockDataService.createCEU(userId, ceuData);
    }

    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }

    try {
      // Prepare CEU data
      const ceuToCreate = {
        ...ceuData,
        // Ensure required fields
        userId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      const createdCEU = await apiRequest(`${API_BASE_URL}/users/${userId}/ceus`, {
        method: 'POST',
        body: JSON.stringify(ceuToCreate),
      });

      // Get user profile for cycle tagging
      let userProfile = null;
      try {
        const userResponse = await apiRequest(`${API_BASE_URL}/users/${userId}`);
        userProfile = userResponse;
      } catch (userError) {
        console.warn('Could not fetch user profile for cycle tagging:', userError.message);
      }

      // Apply cycle tagging with user profile
      return userProfile ? autoTagCycleFields(createdCEU, userProfile) : createdCEU;
    } catch (error) {
      throw error;
    }
  },

  // Update CEU
  async updateCEU(userId, ceuId, updates) {
    if (shouldUseMockData()) {
      return mockDataService.updateCEU(userId, ceuId, updates);
    }

    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }

    try {
      // Prepare update data
      const updateData = {
        ...updates,
        userId,
        ceuId,
        updatedAt: new Date().toISOString(),
      };

      const updatedCEU = await apiRequest(`${API_BASE_URL}/users/${userId}/ceus/${ceuId}`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });

      // Get user profile for cycle tagging
      let userProfile = null;
      try {
        const userResponse = await apiRequest(`${API_BASE_URL}/users/${userId}`);
        userProfile = userResponse;
      } catch (userError) {
        console.warn('Could not fetch user profile for cycle tagging:', userError.message);
      }

      // Apply cycle tagging with user profile
      return userProfile ? autoTagCycleFields(updatedCEU, userProfile) : updatedCEU;
    } catch (error) {
      throw error;
    }
  },

  // Delete CEU
  async deleteCEU(userId, ceuId) {
    if (shouldUseMockData()) {
      return mockDataService.deleteCEU(userId, ceuId);
    }

    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }

    try {
      await apiRequest(`${API_BASE_URL}/users/${userId}/ceus/${ceuId}`, {
        method: 'DELETE',
      });

      return true;
    } catch (error) {
      throw error;
    }
  },

  // Batch operations for admin functions
  async getAllCEUs() {
    if (shouldUseMockData()) {
      return await mockDataService.getAllCEUs();
    }

    // Note: This would need a separate admin endpoint
    // For now, fallback to mock data until admin endpoint is implemented
    return await mockDataService.getAllCEUs();
  },

  // Get CEUs by date range
  async getCEUsByDateRange(userId, startDate, endDate) {
    if (shouldUseMockData()) {
      return mockDataService.getCEUsByDateRange(userId, startDate, endDate);
    }

    // Get all CEUs and filter client-side for now
    // In the future, this could be optimized with query parameters
    const allCEUs = await this.getCEUs(userId);
    
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    
    return allCEUs.filter(ceu => {
      const ceuDate = dayjs(ceu.date);
      return ceuDate.isAfter(start) && ceuDate.isBefore(end);
    });
  },

  // Search CEUs
  async searchCEUs(userId, searchTerm) {
    if (shouldUseMockData()) {
      return mockDataService.searchCEUs(userId, searchTerm);
    }

    // Get all CEUs and search client-side for now
    const allCEUs = await this.getCEUs(userId);
    
    const searchLower = searchTerm.toLowerCase();
    return allCEUs.filter(ceu => 
      ceu.title?.toLowerCase().includes(searchLower) ||
      ceu.description?.toLowerCase().includes(searchLower) ||
      ceu.category?.toLowerCase().includes(searchLower) ||
      ceu.provider?.toLowerCase().includes(searchLower)
    );
  },

  // Get CEU statistics
  async getCEUStats(userId) {
    if (shouldUseMockData()) {
      return mockDataService.getCEUStats(userId);
    }

    // Get all CEUs and calculate stats client-side
    const allCEUs = await this.getCEUs(userId);
    
    const totalHours = allCEUs.reduce((sum, ceu) => sum + (parseFloat(ceu.ceuHours) || 0), 0);
    const totalCEUs = allCEUs.length;
    
    const categoryStats = allCEUs.reduce((stats, ceu) => {
      const category = ceu.category || 'Uncategorized';
      stats[category] = (stats[category] || 0) + (parseFloat(ceu.ceuHours) || 0);
      return stats;
    }, {});

    return {
      totalHours,
      totalCEUs,
      categoryStats,
      recentCEUs: allCEUs
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 5)
    };
  }
};

export default apiCEUService;
